import { intl } from '@beelday/common'
import { useSelector } from 'app-redux'
import { selectAudioStreamState } from 'features/audio-streaming/audio-streaming-redux'
import { selectYouTubeStreamState } from 'features/youtube-streaming/youtube-streaming-redux'
import { ConfiguredGroupWork } from './configured-group-work'
import { ConfiguredKnowledgeCheck } from './configured-knowledge-check'
import { ConfiguredMultipleChoice } from './configured-multiple-choice'
import { ConfiguredOpenQuestions } from './configured-open-questions'
import { ConfiguredQuiz } from './configured-quiz'
import { ConfiguredWordCloud } from './configured-word-cloud'
import { Stage } from './model/stage'
import {
	PresentationIllustration,
	SpeakerIllustration,
} from './presentation/illustrations'
import { Rest } from './presentation/primitives'

const Presenting = (): JSX.Element => (
	<Rest
		icon={<PresentationIllustration />}
		title={<intl.Translate>in_person.mobile.idle.title</intl.Translate>}
		description={
			<intl.Translate>in_person.mobile.idle.description</intl.Translate>
		}
		testId="in_person.mobile.idle"
	/>
)

type MediaProps = {
	/** What the trainer put on, so the room knows this screen is not stuck. */
	name?: string
	paused: boolean
	/** Said instead of the name when the trainer gave none. */
	titleKey: string
}

/**
 * The same three things the in-person laptop says (`PlayingInTheRoom`):
 * whether it plays, what plays, and that there is nothing to do here.
 */
const MediaPlaying = ({ name, paused, titleKey }: MediaProps): JSX.Element => (
	<Rest
		icon={<SpeakerIllustration />}
		pulse={!paused}
		eyebrow={
			<intl.Translate>
				{paused ? 'in_person.media.paused' : 'in_person.media.now_playing'}
			</intl.Translate>
		}
		title={name || <intl.Translate>{titleKey}</intl.Translate>}
		description={<intl.Translate>in_person.media.description</intl.Translate>}
		testId="in_person.mobile.media"
	/>
)

const AudioPlaying = (): JSX.Element => {
	const audio = useSelector(selectAudioStreamState)
	return (
		<MediaPlaying
			name={audio?.name}
			paused={audio?.state === 'Paused'}
			titleKey="in_person.media.audio.title"
		/>
	)
}

const YouTubePlaying = (): JSX.Element => {
	const video = useSelector(selectYouTubeStreamState)
	return (
		<MediaPlaying
			name={video?.name}
			paused={video?.state === 'Paused'}
			titleKey="in_person.media.youtube.title"
		/>
	)
}

const StageContent = ({ stage }: { stage: Stage }): JSX.Element => {
	switch (stage.kind) {
		case 'presenting':
			return <Presenting />
		case 'audio':
			return <AudioPlaying />
		case 'youtube':
			return <YouTubePlaying />
		case 'multipleChoice':
			return <ConfiguredMultipleChoice />
		case 'wordCloud':
			return <ConfiguredWordCloud />
		case 'openQuestions':
			return <ConfiguredOpenQuestions />
		case 'knowledgeCheck':
			return (
				<ConfiguredKnowledgeCheck knowledgeCheckId={stage.knowledgeCheckId} />
			)
		case 'groupWork':
			return <ConfiguredGroupWork />
		case 'quiz':
			return <ConfiguredQuiz />
	}
}

type Props = {
	stage: Stage
}

/**
 * The phone's stage for the focused feature (see `model/stage.ts`). Keyed on
 * the activity: the next poll of the same kind is a fresh component, not the
 * previous one still holding its selection and "sent" state.
 */
export const MobileStage = ({ stage }: Props): JSX.Element => (
	<StageContent key={`${stage.kind}:${stage.key}`} stage={stage} />
)

export default MobileStage
